import React, { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { ministryInfo } from '../mock';
import { Heart, Gift, DollarSign, Users, BookOpen, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export const Donations = () => {
  const [selectedAmount, setSelectedAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [donorInfo, setDonorInfo] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showPayPal, setShowPayPal] = useState(false);

  const presetAmounts = ['25', '50', '100', '250'];

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setShowPayPal(true);
  };

  const handleCustomAmount = (e) => {
    setCustomAmount(e.target.value);
    setSelectedAmount('');
    if (e.target.value && parseFloat(e.target.value) > 0) {
      setShowPayPal(true);
    } else {
      setShowPayPal(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDonorInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getAmount = () => {
    return parseFloat(selectedAmount || customAmount);
  };

  const createPayPalOrder = async () => {
    const amount = getAmount();
    
    if (!amount || amount <= 0) {
      throw new Error('Please select or enter a valid donation amount.');
    }

    try {
      const response = await fetch(`${API}/donation/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          donor_name: donorInfo.name || null,
          donor_email: donorInfo.email || null,
          message: donorInfo.message || null,
          return_url: `${window.location.origin}/donation-success`,
          cancel_url: `${window.location.origin}/donation-cancelled`
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create payment');
      }

      return data.payment_id;
    } catch (error) {
      console.error('Error creating PayPal order:', error);
      throw error;
    }
  };

  const onPayPalApprove = async (data) => {
    try {
      setIsSubmitting(true);
      
      const response = await fetch(`${API}/donation/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_id: data.orderID,
          payer_id: data.payerID,
          donor_name: donorInfo.name || null,
          donor_email: donorInfo.email || null,
          message: donorInfo.message || null
        })
      });

      const result = await response.json();
      
      if (response.ok) {
        setSubmitMessage(`✅ ${result.message}`);
        // Reset form
        setSelectedAmount('');
        setCustomAmount('');
        setDonorInfo({ name: '', email: '', message: '' });
        setShowPayPal(false);
      } else {
        throw new Error(result.detail || 'Payment execution failed');
      }
    } catch (error) {
      console.error('PayPal approval error:', error);
      setSubmitMessage(`❌ Payment failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onPayPalError = (error) => {
    console.error('PayPal error:', error);
    setSubmitMessage('❌ Payment failed. Please try again.');
    setIsSubmitting(false);
  };

  const paypalOptions = {
    "client-id": PAYPAL_CLIENT_ID,
    currency: "USD",
    intent: "capture",
  };

  return (
    <section id="donations" className="section-padding" style={{ background: 'linear-gradient(135deg, #FFF8DC 0%, #F5F5DC 100%)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '24px' }}>
            <Heart size={32} style={{ color: '#8B4513' }} />
            <h2 className="hero-medium" style={{ color: '#8B4513' }}>Support Our Ministry</h2>
          </div>
          
          <p className="body-large" style={{ color: '#5D4037', maxWidth: '700px', margin: '0 auto' }}>
            Your generous donations help us keep our daily devotionals free and support those who need healing, 
            restoration, and transformation but may not have the financial means to access our programs.
          </p>
        </div>

        <div className="grid-two-column" style={{ alignItems: 'flex-start', gap: '60px' }}>
          {/* Impact Section */}
          <div>
            <h3 className="heading-2" style={{ marginBottom: '32px' }}>
              Your Impact
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px'
              }}>
                <BookOpen size={24} style={{ color: 'var(--text-primary)', marginTop: '4px' }} />
                <div>
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>
                    Free Daily Devotionals
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Keep our devotionals free for everyone who needs daily encouragement and biblical wisdom in their healing journey.
                  </p>
                </div>
              </div>

              <div style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px'
              }}>
                <Users size={24} style={{ color: 'var(--text-primary)', marginTop: '4px' }} />
                <div>
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>
                    Scholarship Programs
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Provide reduced-cost coaching sessions and programs for those experiencing financial hardship but desperately need healing.
                  </p>
                </div>
              </div>

              <div style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px'
              }}>
                <Calendar size={24} style={{ color: 'var(--text-primary)', marginTop: '4px' }} />
                <div>
                  <h4 className="heading-3" style={{ marginBottom: '8px' }}>
                    Ministry Expansion
                  </h4>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    Help us reach more people with the message of healing, restoration, and God's truth where brokenness has taken root.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ 
              background: 'var(--bg-subtle)', 
              padding: '24px', 
              borderRadius: '0px',
              marginTop: '32px'
            }}>
              <p className="body-regular" style={{ color: 'var(--text-primary)', textAlign: 'center', fontStyle: 'italic' }}>
                "Every gift, no matter the size, helps repair the breach and restore lives on the foundation of Truth."
              </p>
            </div>
          </div>

          {/* Donation Form */}
          <div style={{
            background: 'var(--bg-secondary)',
            padding: '40px',
            borderRadius: '0px',
            border: '1px solid var(--border-light)'
          }}>
            <h3 className="heading-2" style={{ marginBottom: '24px', textAlign: 'center' }}>
              Make a Donation
            </h3>

            {submitMessage && (
              <div style={{
                padding: '16px',
                marginBottom: '24px',
                background: submitMessage.includes('✅') ? 'var(--bg-subtle)' : 'var(--status-error)',
                color: submitMessage.includes('✅') ? 'var(--status-success)' : 'var(--bg-primary)',
                borderRadius: '0px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                {submitMessage}
              </div>
            )}

            {/* Amount Selection */}
            <div style={{ marginBottom: '24px' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>
                Select Amount
              </label>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
                {presetAmounts.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => handleAmountSelect(amount)}
                    disabled={isSubmitting}
                    style={{
                      padding: '12px',
                      border: selectedAmount === amount ? '2px solid var(--interactive-base)' : '1px solid var(--border-medium)',
                      borderRadius: '0px',
                      background: selectedAmount === amount ? 'var(--bg-subtle)' : 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: '14px',
                      fontFamily: 'Crimson Text, serif',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: isSubmitting ? 0.7 : 1
                    }}
                  >
                    ${amount}
                  </button>
                ))}
              </div>

              <div style={{ position: 'relative' }}>
                <DollarSign 
                  size={18} 
                  style={{ 
                    position: 'absolute', 
                    left: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: 'var(--text-secondary)'
                  }} 
                />
                <input
                  type="number"
                  placeholder="Custom amount"
                  value={customAmount}
                  onChange={handleCustomAmount}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '12px 16px 12px 36px',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '0px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                />
              </div>
            </div>

            {/* Donor Information */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                  Name (Optional)
                </label>
                <input
                  type="text"
                  name="name"
                  value={donorInfo.name}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '0px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                  Email (Optional)
                </label>
                <input
                  type="email"
                  name="email"
                  value={donorInfo.email}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '0px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px' }}>
                  Message (Optional)
                </label>
                <textarea
                  name="message"
                  rows="3"
                  value={donorInfo.message}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  placeholder="Share why you're supporting this ministry..."
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid var(--border-medium)',
                    borderRadius: '0px',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px',
                    fontFamily: 'Crimson Text, serif',
                    resize: 'vertical',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                />
              </div>
            </div>

            {/* PayPal Button */}
            {showPayPal && PAYPAL_CLIENT_ID && (
              <div style={{ marginBottom: '16px' }}>
                <PayPalScriptProvider options={paypalOptions}>
                  <PayPalButtons
                    createOrder={createPayPalOrder}
                    onApprove={onPayPalApprove}
                    onError={onPayPalError}
                    onCancel={() => {
                      setSubmitMessage('❌ Payment was cancelled.');
                    }}
                    style={{ 
                      layout: 'vertical',
                      color: 'blue',
                      shape: 'rect',
                      label: 'donate'
                    }}
                    disabled={isSubmitting}
                  />
                </PayPalScriptProvider>
              </div>
            )}

            {!showPayPal && (
              <p className="body-regular" style={{ 
                color: 'var(--text-secondary)', 
                textAlign: 'center', 
                padding: '20px',
                background: 'var(--bg-subtle)',
                borderRadius: '0px',
                fontStyle: 'italic'
              }}>
                Select an amount above to see payment options
              </p>
            )}

            <p className="body-small" style={{ 
              color: 'var(--text-secondary)', 
              textAlign: 'center', 
              marginTop: '16px',
              lineHeight: 1.4
            }}>
              Your donation helps keep our devotionals free and supports those in need. 
              All donations are processed securely through PayPal.
            </p>
          </div>
        </div>

        {/* Gratitude Section */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '80px',
          background: 'var(--bg-secondary)',
          padding: '48px',
          borderRadius: '0px'
        }}>
          <h3 className="heading-2" style={{ marginBottom: '16px' }}>
            Thank You for Your Heart
          </h3>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            Every donation, whether $5 or $500, helps us stand in the gap and bring God's healing where brokenness has taken root. 
            Your generosity makes transformation possible for those who need it most.
          </p>
        </div>
      </div>
    </section>
  );
};